import { useGetGiveaway } from '@bpm-web-app/swr-hooks';
import { appendQueryParams, useHubSwitch } from '@bpm-web-app/utils';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Fragment, useMemo } from 'react';
import { AppLink } from '@bpm-web-app/components/shared';
import styles from './giveaway-detail.module.css';
import SocialShare from '../../shared/social-share/social-share';
import PrizeCard from '../../prize-card/prize-card';
import GenericCarousel from '../../shared/ui/generic-carousel/generic-carousel';
import CurrentContests from '../../current-contests/current-contests';
import PastContests from '../../past-contests/past-contests';
import Comments from '../../comments/comments';
import ShareIcon from '../../../assets/icons/share-icon.svg';
import Title from '../../title/title';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface GiveawayDetailProps { }

export function GiveawayDetail(props: GiveawayDetailProps) {
    const router = useRouter();
    const { slug } = router.query;
    const { data, isLoading, error } = useGetGiveaway(slug as string);
    const { hub } = useHubSwitch();

    const isExpired = useMemo(() => dayjs(data?.entry_deadline).isBefore(new Date()), [data]);

    /* TODO: implement (ghost?) loading */
    if (isLoading) return null;

    if (!isLoading && error) return null;

    return (
        data && (
            <>
                <Title platform={hub} title={data.title} />
                <div className={styles['giveaway-detail']}>
                    {data.banner_image_url && (
                        <div className={styles['giveaway-detail__img-wrapper']}>
                            <picture className={styles['giveaway-detail__img']}>
                                <img
                                    src={appendQueryParams(data.banner_image_url, { key: 'dw', value: 1200 })}
                                    srcSet={`${appendQueryParams(data.banner_image_url, { key: 'dw', value: 2400 })} 2x`}
                                    alt={data.title}
                                />
                            </picture>
                        </div>
                    )}
                    <div className={styles['giveaway-detail__intro']}>
                        <div className={styles['giveaway-detail__summary']}>
                            <h1 className={styles['giveaway-detail__title']}>{data.title}</h1>
                            <ul className={styles['giveaway-detail__details']}>
                                <li className={styles['giveaway-detail__detail']}>
                                    Submission Start Date
                                    <strong>{dayjs(data.start_date).format('MMMM D, YYYY, hh:mma')}</strong>
                                </li>
                                <li className={styles['giveaway-detail__detail']}>
                                    Submission Deadline
                                    <strong>{dayjs(data.entry_deadline).format('MMMM D, YYYY, hh:mma')}</strong>
                                </li>
                                <li className={styles['giveaway-detail__detail']}>
                                    Winner Announced
                                    <strong>{dayjs(data.announcement).format('MMMM D, YYYY, hh:mma')}</strong>
                                </li>
                            </ul>
                            <div className={styles['giveaway-detail__summary-ctas']}>
                                {isExpired ?
                                    <div className={classNames(styles['giveaway-detail__summary-cta'], styles['giveaway-detail__summary-cta--primary'], styles['giveaway-detail__summary-cta--text'], styles['giveaway-detail__summary-cta--disabled'])}>
                                        Enter Giveaway
                                    </div>
                                    : (
                                        <AppLink href={`/giveaway/${slug}/enter`}>
                                            <a className={classNames(styles['giveaway-detail__summary-cta'], styles['giveaway-detail__summary-cta--primary'], styles['giveaway-detail__summary-cta--text'])}>
                                                Enter Giveaway
                                            </a>
                                        </AppLink>
                                    )
                                }
                            </div>
                        </div>
                        <div className={styles['giveaway-detail__content']}>
                            <div className={styles['giveaway-detail__description']} dangerouslySetInnerHTML={{ __html: data.description }} />
                            <a href={data.terms_pdf_url} className={styles['giveaway-detail__link']}>
                                See Terms & Conditions
                            </a>
                            <div className={styles['giveaway-detail__share']}>
                                <SocialShare platforms={['facebook', 'twitter', 'linkedin', 'link', 'email']} url={data.share_url} title="Share" icon={<ShareIcon />} />
                            </div>
                        </div>
                    </div>
                    <GenericCarousel title="Prizes">
                        {data.GiveawayPrizes.map((prize: any) => (
                            <Fragment key={prize.id}>
                                <PrizeCard id={prize.id} image={prize.image_url} title={prize.title} description={prize.description} link={prize.link} />
                            </Fragment>
                        ))}
                    </GenericCarousel>
                    <Comments giveawayId={data.id} />
                    <CurrentContests />
                    <PastContests />
                </div>
            </>
        )
    );
}

export default GiveawayDetail;
